import * as React from "react";
import { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import { Button, Card, CircularProgress } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { timesheetService } from "src/app/services/api/timesheet.service";
import { useDispatch } from "react-redux";
import { showMessage } from "app/store/fuse/messageSlice";
import {
  TimeSheetData,
  TimesheetEntry,
} from "src/app/@types/interfaces/timesheet.interface";

// ——— Extend the row type locally to include a unique `id` ———
type RowData = TimeSheetData & { id: string };

const ColorButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.black,
  color: theme.palette.common.white,
  "&:hover": {
    backgroundColor: blueGrey[900],
  },
}));

// Convert the raw entry + an id into our RowData
function createDataFromTimesheet(entry: TimesheetEntry, id: string): RowData {
  return {
    id,
    program: entry.Program,
    worksite: entry.Worksite,
    date: entry.Date,
    youthId: entry["Youth ID"],
    youthName: entry["Youth Name"],
    hoursWorked: entry["Hours Worked"],
    workDescription: entry["Work Description"],
    status: entry.Status,
    batchId: entry["Batch ID"],
  };
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    // color: theme.palette.text.primary,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof RowData;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  { id: "program", numeric: false, disablePadding: true, label: "Program" },
  { id: "worksite", numeric: false, disablePadding: false, label: "Worksite" },
  { id: "date", numeric: false, disablePadding: false, label: "Date" },
  { id: "youthId", numeric: false, disablePadding: false, label: "Youth ID" },
  {
    id: "youthName",
    numeric: false,
    disablePadding: false,
    label: "Youth Name",
  },
  {
    id: "hoursWorked",
    numeric: true,
    disablePadding: false,
    label: "Hours Worked",
  },
  {
    id: "workDescription",
    numeric: false,
    disablePadding: false,
    label: "Work Description",
  },
  { id: "status", numeric: false, disablePadding: false, label: "Status" },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof RowData
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

// Updated to match TimeSheetPage interface structure
interface TimeSheetTableProps {
  filters?: {
    week: string | null;
    worksite: string | null;
    status: string | null;
    program: string | null;
    name: string | null;
    id: string | null;
  };
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof RowData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <StyledTableRow>
        <StyledTableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all timesheets" }}
          />
        </StyledTableCell>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              <span style={{ fontWeight: "bold", color: "white" }}>
                {headCell.label}
              </span>
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </StyledTableRow>
    </TableHead>
  );
}

export default function TimeSheetTable({ filters }: TimeSheetTableProps) {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof RowData>("date");
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<RowData[]>([]);
  const [allData, setAllData] = useState<RowData[]>([]);
  const dispatch = useDispatch();

  // Load & index each entry
  useEffect(() => {
    const fetchTimesheetData = async () => {
      try {
        setLoading(true);
        const response = await timesheetService.getAllTimesheets();
        if (response.success && response.data.timesheet) {
          const formatted: RowData[] = response.data.timesheet.map(
            (entry, idx) => createDataFromTimesheet(entry, idx.toString())
          );
          setAllData(formatted);
          setRows(formatted);
        }
      } catch (error) {
        dispatch(
          showMessage({
            message: "Failed to load timesheet data",
            variant: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    };
    fetchTimesheetData();
  }, [dispatch]);

  // Updated filters to handle null values properly
  useEffect(() => {
    if (!filters) return;
    setPage(0);
    let filtered = [...allData];

    // Only apply filters that are non-null and non-empty
    if (filters.program && filters.program.trim() !== "") {
      filtered = filtered.filter((r) =>
        r.program.toLowerCase().includes(filters.program!.toLowerCase())
      );
    }

    if (filters.worksite && filters.worksite.trim() !== "") {
      filtered = filtered.filter((r) =>
        r.worksite.toLowerCase().includes(filters.worksite!.toLowerCase())
      );
    }

    if (filters.status && filters.status.trim() !== "") {
      filtered = filtered.filter(
        (r) => r.status.toLowerCase() === filters.status!.toLowerCase()
      );
    }

    if (filters.name && filters.name.trim() !== "") {
      filtered = filtered.filter((r) =>
        r.youthName.toLowerCase().includes(filters.name!.toLowerCase())
      );
    }

    if (filters.id && filters.id.trim() !== "") {
      filtered = filtered.filter((r) =>
        r.youthId.toLowerCase().includes(filters.id!.toLowerCase())
      );
    }

    if (filters.week && filters.week.trim() !== "") {
      console.log(`Filtering by week: ${filters.week}`);

      filtered = filtered.filter((r) => {
        const weekText = filters.week!.toLowerCase().replace("week ", "");
        console.log(`Filtering by week: ${weekText}`);

        return r.date.toLowerCase().includes(weekText);
      });
    }

    setRows(filtered);
    setSelected([]);
  }, [filters, allData]);

  // Sort handlers
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof RowData
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // When "select all" clicked
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((r) => r.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  // Fixed checkbox click handler to properly manage selection
  const handleCheckboxClick = (
    event: React.MouseEvent<unknown> | React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    event.stopPropagation();
    event.preventDefault(); // Prevent default behavior

    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      // Add to selection if not already selected
      newSelected = [...selected, id];
    } else {
      // Remove from selection if already selected
      newSelected = [...selected];
      newSelected.splice(selectedIndex, 1);
    }

    setSelected(newSelected);
  };

  // Handle row click - now using the same logic as checkbox click for consistency
  const handleRowClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      // Add to selection if not already selected
      newSelected = [...selected, id];
    } else {
      // Remove from selection if already selected
      newSelected = [...selected];
      newSelected.splice(selectedIndex, 1);
    }

    setSelected(newSelected);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Approve the selected timesheets
  const handleApprove = () => {
    try {
      // Implementation for approving selected timesheets
      // In a real application, this would typically call an API endpoint

      // Update both state arrays to ensure consistency
      const updatedRows = rows.map((row) => {
        if (selected.includes(row.id)) {
          return { ...row, status: "Approved" };
        }
        return row;
      });

      const updatedAllData = allData.map((row) => {
        if (selected.includes(row.id)) {
          return { ...row, status: "Approved" };
        }
        return row;
      });

      // Update state with the new data
      setRows(updatedRows);
      setAllData(updatedAllData);

      // Show success message
      dispatch(
        showMessage({
          message: `Approved ${selected.length} timesheets`,
          variant: "success",
        })
      );

      // Clear selection after approval
      setSelected([]);
    } catch (error) {
      dispatch(
        showMessage({
          message: "Failed to approve timesheets",
          variant: "error",
        })
      );
    }
  };

  // Reject the selected timesheets
  const handleReject = () => {
    try {
      // Update both state arrays to ensure consistency
      const updatedRows = rows.map((row) => {
        if (selected.includes(row.id)) {
          return { ...row, status: "Rejected" };
        }
        return row;
      });

      const updatedAllData = allData.map((row) => {
        if (selected.includes(row.id)) {
          return { ...row, status: "Rejected" };
        }
        return row;
      });

      // Update state with the new data
      setRows(updatedRows);
      setAllData(updatedAllData);

      // Show success message
      dispatch(
        showMessage({
          message: `Rejected ${selected.length} timesheets`,
          variant: "info",
        })
      );

      // Clear selection after rejection
      setSelected([]);
    } catch (error) {
      dispatch(
        showMessage({
          message: "Failed to reject timesheets",
          variant: "error",
        })
      );
    }
  };

  const handleDownload = () => {
    // Implementation for downloading timesheet data
    dispatch(showMessage({ message: "Download initiated", variant: "info" }));
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const visibleRows = React.useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full m-24">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className="m-24">
        <TableContainer component={Paper}>
          <Table aria-label="timesheet table">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.length > 0 ? (
                visibleRows.map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <StyledTableRow
                      hover
                      onClick={(e) => handleRowClick(e, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      key={row.id}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer" }}
                    >
                      <StyledTableCell
                        padding="checkbox"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxClick(e, row.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        component="th"
                        scope="row"
                        padding="none"
                      >
                        {row.program}
                      </StyledTableCell>
                      <StyledTableCell>{row.worksite}</StyledTableCell>
                      <StyledTableCell>{row.date}</StyledTableCell>
                      <StyledTableCell>{row.youthId}</StyledTableCell>
                      <StyledTableCell>{row.youthName}</StyledTableCell>
                      <StyledTableCell align="right">
                        {row.hoursWorked}
                      </StyledTableCell>
                      <StyledTableCell>{row.workDescription}</StyledTableCell>
                      <StyledTableCell>{row.status}</StyledTableCell>
                    </StyledTableRow>
                  );
                })
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={9} align="center">
                    No records found
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </div>

      <Card variant="outlined" className="mb-5 ml-24 mr-24">
        <div className="flex flex-row gap-16 mb-24 mt-24 mx-16">
          <ColorButton
            variant="contained"
            color="primary"
            disabled={selected.length === 0}
            onClick={handleApprove}
          >
            Approve
          </ColorButton>
          <ColorButton
            variant="contained"
            color="primary"
            disabled={selected.length === 0}
            onClick={handleReject}
          >
            Reject
          </ColorButton>
          <ColorButton
            variant="contained"
            color="primary"
            disabled={rows.length === 0}
            onClick={handleDownload}
          >
            Download
          </ColorButton>
        </div>
      </Card>
    </>
  );
}
